/**
 * Structural time operations on a track's key list (W1-10b core-commands-motion; DESIGN.md §5.4, §5.6.5, §6.4).
 *
 *  - `replaceSpan`: the paste core. The target is split shape-preservingly at the span edges (so the curve outside
 *    the span is unchanged), then everything strictly inside is replaced by the slice; the edge keys keep the
 *    target's arrive value (start) and leave value / curve (end), and take the slice's leave value (start) and
 *    arrive value (end): jumps appear where the two curves differ. A one-key slice has no span: it sets the key at
 *    its beat like an inserted key with a value (CR-W1-10b-11).
 *  - `reverseWindow`: exact time reversal of the curve inside [s, e] (keys mirrored t -> s + e - t, arrive and
 *    leave values swapped, eases reversed f -> 1 - f(1 - x)); keys are inserted at s and e first when needed.
 *    Segment progress is in seconds, so a segment whose own span or mirrored span contains a tempo change would not
 *    reverse exactly: such segments are split shape-preservingly first at the tempo changes inside the window and
 *    at their mirror images (`tempoCuts`), after which every piece eases proportionally to beats on both sides.
 *    Each reversed segment is then checked as Phira plays it (`checkReversed`): one that f32 cannot reverse (a slice
 *    collapsing near 1, a Bezier solver) is baked from the mirrored original, and the deviation is reported.
 *  - `insertTimeKeys` / `removeTimeKeys`: insert or remove a time range. Inserting moves every key at or after
 *    `at` later (a segment spanning `at` is stretched); removing a range that contains no key shrinks the spanning
 *    segment (the exact inverse), and a range that contains keys is cut: split at both edges, the inside deleted,
 *    the two sides joined at `from` (arrive value from the left side, leave value from the right side). On a line
 *    with position X/Y linked, an axis whose cut joins two sides forces the same cut on the other axis
 *    (`forceCut`), so both axes keep their keys at the same beats.
 *  - `sliceBetween`: the keys of [s, e] as a slice for `replaceSpan` (moving segments between layers).
 *  - `explicitLeadIn`: where Phira extrapolates the first segment backwards (an imported lead-in: the first key is
 *    after beat 0), the curve before the first key depends on that segment. `replaceSpan`, `sliceBetween`,
 *    `reverseWindow`, a cut of `removeTimeKeys` and a bake of the first segment (keys.ts `bakeSegments`) first turn
 *    that lead-in into an explicit segment from beat 0 when their span starts at or before the first key, so the
 *    curve before the span stays as it was.
 *  - `captureLeadIn` / `leadInShiftReport`: inserting time before or inside the first segment of such a track, or
 *    shrinking time there, moves or stretches that segment (insert then remove of the same range stays the identity), so the
 *    visible lead-in moves with it; the change is measured and reported (`approx: 'leadInShift'`, CR-W1-10b-12).
 * Every operation that splits a curve returns the `SplitReport` of its splits (the command notes approximations).
 */

import { Ease, HOLD } from '../ease/index';
import type { Ease as EaseValue } from '../ease/types';
import { Beats } from '../time/beat';
import type { Beat } from '../time/types';
import type { TrackValue } from '../model/types';
import { indexAt, indexAtOrBefore, newRec, sameValue, type KeyRec } from './motionTrack';
import { TEXT_EXACT_SHIFT_SEC, textRunsShared, textShift, type EvalBudget } from './textDisplay';
import {
  adaptiveBakeCuts,
  BAKE_TOLERANCE,
  bakeSegmentWith,
  displayedText,
  displayOn,
  EXACT_REL_ERROR,
  EXACT_SPLIT,
  exactNoise,
  insertKeyRec,
  NON_FINITE_CHANGE,
  playedRaw,
  rawFinite,
  playedNoise,
  segmentRaw,
  staircaseChange,
  valueDiff,
  valueSpan,
  worseSplit,
  type CurveCtx,
  type RawValue,
  type SplitReport,
} from './splitKey';

function unlinkInto(k: KeyRec, unlinked: Set<number>): void {
  if (k.link !== 0) {
    unlinked.add(k.link);
    k.link = 0;
  }
}

/**
 * True when Phira shows the first segment continued backwards before the first key (the track's lead-in rule is
 * 'extrapolate' and its first key lies after beat 0) and an operation changing the curve from `s` on (0 < s, at or
 * before the first key) would make another segment the one that is continued.
 */
function leadInBeforeSpan(keys: readonly KeyRec[], s: Beat, ctx: CurveCtx): boolean {
  const first = keys[0];
  if (first === undefined || ctx.leadIn !== 'extrapolate') return false;
  return Beats.lt(Beats.ZERO, s) && Beats.lt(Beats.ZERO, first.t) && Beats.lte(s, first.t);
}

/**
 * Makes the visible lead-in of a track explicit before an operation changes its curve from `s` on (s > 0). When the
 * track's lead-in rule is 'extrapolate' and its first key lies after beat 0 (an imported lead-in, `importedLeadIn`),
 * Phira shows the first segment's curve continued backwards in [0, first key); an operation whose span starts at
 * or before the first key would make a different segment the first one, and that one would be extrapolated
 * instead. A key at beat 0 that continues the lead-in (`insertKeyRec`: exact for a straight or constant first
 * segment, otherwise a straight segment whose deviation is measured and reported as `approx: 'leadIn'`) turns
 * [0, first key) into a segment of its own that the span does not touch; `commitKeys` then clears
 * `importedLeadIn`. Returns the report of that insertion (exact when nothing had to be inserted).
 */
export function explicitLeadIn(keys: KeyRec[], s: Beat, ctx: CurveCtx, unlinked: Set<number>): SplitReport {
  if (!leadInBeforeSpan(keys, s, ctx)) return EXACT_SPLIT;
  return insertKeyRec(keys, Beats.ZERO, ctx, unlinked);
}

/** A key of a slice to paste (absolute beats). */
export interface SliceKey {
  readonly t: Beat;
  readonly vIn: TrackValue;
  readonly vOut: TrackValue;
  readonly ease: EaseValue;
  readonly link: number;
}

/**
 * Replaces the curve of `keys` inside [slice[0].t, slice[last].t] by the slice (see the file header). A single-key
 * slice sets the key at that beat (leave value, and arrive value unless it follows a Stay) as an inserted key with
 * that value would: the neighbouring segments keep their curves and end at the new value (a half that cannot keep
 * its curve with the new value becomes straight, reported as `approx: 'linearHalf'`). An extrapolated lead-in
 * before the span is made explicit first (`explicitLeadIn`). Returns the report of the edge splits (and of that
 * lead-in key).
 */
export function replaceSpan(
  keys: KeyRec[],
  slice: readonly SliceKey[],
  ctx: CurveCtx,
  unlinked: Set<number>,
): SplitReport {
  const first = slice[0];
  const last = slice[slice.length - 1];
  if (first === undefined || last === undefined) return EXACT_SPLIT;
  if (keys.length === 0) {
    for (const s of slice) keys.push(newRec(s.t, s.vIn, s.vOut, s.ease, s.link));
    return EXACT_SPLIT;
  }
  const lead = explicitLeadIn(keys, first.t, ctx, unlinked);
  if (slice.length === 1) {
    const r = insertKeyRec(keys, first.t, ctx, unlinked, first.vOut);
    const k = keys[r.index] as KeyRec;
    const prev = keys[r.index - 1];
    // A jump the slice carried (a key copied as a point); after a Stay the arrive value is the stayed value.
    if (prev !== undefined && prev.ease.kind !== 'hold') k.vIn = first.vIn;
    return worseSplit(lead, r);
  }
  const report = worseSplit(
    lead,
    worseSplit(insertKeyRec(keys, first.t, ctx, unlinked), insertKeyRec(keys, last.t, ctx, unlinked)),
  );
  const iS = indexAt(keys, first.t);
  const iE = indexAt(keys, last.t);
  const ks = keys[iS] as KeyRec;
  const ke = keys[iE] as KeyRec;
  for (let i = iS; i < iE; i++) unlinkInto(keys[i] as KeyRec, unlinked);
  const start: KeyRec = {
    id: ks.id,
    t: first.t,
    vIn: iS === 0 ? first.vOut : ks.vIn,
    vOut: first.vOut,
    ease: first.ease,
    link: first.link,
    src: undefined,
  };
  const middle = slice.slice(1, -1).map((s) => newRec(s.t, s.vIn, s.vOut, s.ease, s.link));
  const end: KeyRec = { ...ke, vIn: last.vIn };
  keys.splice(iS, iE - iS + 1, start, ...middle, end);
  return report;
}

/**
 * Splits `keys` shape-preservingly at s and e (s < e) and returns the keys of [s, e] as a slice: the first key's
 * leave value and curve, the inner keys as they are, the last key's arrive value (links kept). An extrapolated
 * lead-in before s is made explicit first (`explicitLeadIn`), so replacing [s, e] later keeps the curve before s.
 * Also returns the report of the splits.
 */
export function sliceBetween(
  keys: KeyRec[],
  s: Beat,
  e: Beat,
  ctx: CurveCtx,
  unlinked: Set<number>,
): { readonly slice: SliceKey[]; readonly report: SplitReport } {
  const lead = explicitLeadIn(keys, s, ctx, unlinked);
  const report = worseSplit(
    lead,
    worseSplit(insertKeyRec(keys, s, ctx, unlinked), insertKeyRec(keys, e, ctx, unlinked)),
  );
  const iS = indexAt(keys, s);
  const iE = indexAt(keys, e);
  const slice = keys.slice(iS, iE + 1).map((k, j, arr): SliceKey => ({
    t: k.t,
    vIn: j === 0 ? k.vOut : k.vIn,
    vOut: j === arr.length - 1 ? k.vIn : k.vOut,
    ease: k.ease,
    link: j === arr.length - 1 ? 0 : k.link,
  }));
  return { slice, report };
}

/**
 * Beats strictly inside (s, e) at which a curve must have a key before it is reversed within [s, e]: the tempo
 * changes inside the window (`tempoBeats`, motionTrack.ts `tempoChangeBeats`) and their mirror images s + e - c.
 * Between two consecutive cuts neither the piece nor its mirror image contains a tempo change, so the piece eases
 * proportionally to beats where it is and where it lands, and reversing its ease is exact.
 */
export function tempoCuts(tempoBeats: readonly Beat[], s: Beat, e: Beat): Beat[] {
  const sum = Beats.add(s, e);
  const out = new Map<string, Beat>();
  for (const c of tempoBeats) {
    if (!Beats.lt(s, c) || !Beats.lt(c, e)) continue;
    const m = Beats.sub(sum, c);
    out.set(Beats.key(c), c);
    out.set(Beats.key(m), m);
  }
  return [...out.values()].sort((a, b) => Beats.cmp(a, b));
}

/**
 * Splits the segment containing `c` shape-preservingly when its shape depends on where the tempo changes (a
 * Stay or a constant segment does not). Returns the report of the split.
 */
function splitAtTempoCut(keys: KeyRec[], c: Beat, ctx: CurveCtx, unlinked: Set<number>): SplitReport {
  const i = indexAtOrBefore(keys, c);
  const k = keys[i];
  const n = keys[i + 1];
  if (k === undefined || n === undefined || Beats.eq(k.t, c)) return EXACT_SPLIT;
  if (k.ease.kind === 'hold' || sameValue(k.vOut, n.vIn)) return EXACT_SPLIT;
  return insertKeyRec(keys, c, ctx, unlinked);
}

/**
 * Exact time reversal of the curve inside [s, e] (s < e); an extrapolated lead-in before s is made explicit first
 * (`explicitLeadIn`), and segments are split at the tempo changes of the window and their mirror images
 * (`tempoCuts`, from `tempoBeats`), so the reversed curve is the beat-mirrored original also where the tempo
 * changes; reversed segments that Phira's f32 evaluation cannot reverse are baked (`checkReversed`). Returns the
 * report of the insertions and of that check.
 */
export function reverseWindow(
  keys: KeyRec[],
  s: Beat,
  e: Beat,
  ctx: CurveCtx,
  unlinked: Set<number>,
  tempoBeats: readonly Beat[],
): SplitReport {
  if (!Beats.lt(s, e) || keys.length === 0) return EXACT_SPLIT;
  // Value ranges of the segments as they are now: a piece split off one of them is measured against its range.
  const ranges = segmentRanges(keys);
  const lead = explicitLeadIn(keys, s, ctx, unlinked);
  let report = worseSplit(lead, worseSplit(insertKeyRec(keys, s, ctx, unlinked), insertKeyRec(keys, e, ctx, unlinked)));
  for (const c of tempoCuts(tempoBeats, s, e)) report = worseSplit(report, splitAtTempoCut(keys, c, ctx, unlinked));
  const iS = indexAt(keys, s);
  const iE = indexAt(keys, e);
  const n = iE - iS;
  if (n < 1) return report;
  const old = keys.slice(iS, iE + 1).map((k) => ({ ...k }));
  const sum = Beats.add(s, e);
  const out: KeyRec[] = [];
  for (let j = 0; j <= n; j++) {
    const src = old[n - j] as KeyRec;
    if (j === 0) {
      const k0 = old[0] as KeyRec;
      unlinkInto(k0, unlinked);
      out.push({ ...k0, vOut: src.vIn, ease: reversedEase(old[n - 1] as KeyRec), link: 0 });
    } else if (j === n) {
      const kn = old[n] as KeyRec;
      out.push({ ...kn, vIn: (old[0] as KeyRec).vOut });
    } else {
      unlinkInto(src, unlinked);
      out.push({
        id: src.id,
        t: Beats.sub(sum, src.t),
        vIn: src.vOut,
        vOut: src.vIn,
        ease: reversedEase(old[n - j - 1] as KeyRec),
        link: 0,
        src: src.src,
      });
    }
  }
  keys.splice(iS, n + 1, ...out);
  return worseSplit(report, checkReversed(keys, iS, old, Beats.toF64(sum), ctx, ranges));
}

/** A segment of a key list and its value range |end - start| (largest component; 0 when not finite). */
interface SegmentRange {
  readonly from: Beat;
  readonly to: Beat;
  readonly range: number;
}

function segmentRanges(keys: readonly KeyRec[]): SegmentRange[] {
  const out: SegmentRange[] = [];
  for (let i = 0; i + 1 < keys.length; i++) {
    const a = keys[i] as KeyRec;
    const b = keys[i + 1] as KeyRec;
    const d = valueDiff(a.vOut, b.vIn);
    out.push({ from: a.t, to: b.t, range: Number.isFinite(d) ? d : 0 });
  }
  return out;
}

/** Range of the segment that contained the piece [from, to] before it was split, or null. */
function rangeOf(ranges: readonly SegmentRange[], from: Beat, to: Beat): number | null {
  for (const r of ranges) if (Beats.lte(r.from, from) && Beats.lte(to, r.to)) return r.range;
  return null;
}

function reversedEase(k: KeyRec): EaseValue {
  return k.ease.kind === 'hold' ? HOLD : Ease.reverse(k.ease);
}

/** Samples of the check of a reversed segment against the mirrored original (midpoints, ends excluded). */
const REVERSE_CHECK_SAMPLES = 64;

/**
 * Checks every reversed segment keys[iS + j] -> keys[iS + j + 1] as Phira PLAYS it (f32, its clamped tweens)
 * against the original segment it mirrors (`old`, the window's keys before the reversal; `sumBeats` = s + e).
 * Reversing an ease is exact on paper, but f32 is not symmetric: a slice ending close to 1 can collapse where the
 * same slice near 0 did not (Phira's clamped tween then divides by zero), and Bezier halves go through Phira's own
 * solver. Deviations are relative to the value range of the segment the piece was split from (`ranges`, as for
 * any split). A segment that strays beyond the bake tolerance (0.1 % of that range) or is not finite is baked into
 * linear pieces sampled from the mirrored original (`adaptiveBakeCuts`); a smaller deviation beyond the f32 noise
 * of both curves (splitKey.ts `playedNoise`: values and slice renormalisers; for Expo / Elastic the values only,
 * `exactNoise`, and a step of the slice's staircase counts as a change, `staircaseChange`) plus EXACT_REL_ERROR of
 * the range makes the result approximate. Text segments are compared by what they show (`checkReversedText`).
 */
function checkReversed(
  keys: KeyRec[],
  iS: number,
  old: readonly KeyRec[],
  sumBeats: number,
  ctx: CurveCtx,
  ranges: readonly SegmentRange[],
): SplitReport {
  if (ctx.info.valueType === 'string') return checkReversedText(keys, iS, old, sumBeats, ctx);
  const n = old.length - 1;
  const tempo = ctx.tempo;
  const quantum = ctx.info.valueType === 'rgb' || ctx.info.valueType === 'rgba8' ? 1 : 0;
  let report = EXACT_SPLIT;
  // Back to front: baking a segment inserts keys after its start only.
  for (let j = n - 1; j >= 0; j--) {
    const a = keys[iS + j];
    const b = keys[iS + j + 1];
    const oa = old[n - j - 1];
    const ob = old[n - j];
    if (a === undefined || b === undefined || oa === undefined || ob === undefined) continue;
    if (a.ease.kind === 'hold' || oa.ease.kind === 'hold') continue;
    const ta = tempo.secAt(a.t);
    const tb = tempo.secAt(b.t);
    const oA = tempo.secAt(oa.t);
    const oB = tempo.secAt(ob.t);
    if (!(tb > ta) || !(oB > oA)) continue;
    const mirroredX = (sec: number): number => (tempo.secAtBeatF64(sumBeats - tempo.beatF64At(sec)) - oA) / (oB - oA);
    // Deviations are relative to the range of the segment the piece was split from (as for any split).
    const own = valueDiff(a.vOut, b.vIn);
    const span = Math.max(rangeOf(ranges, oa.t, ob.t) ?? 0, Number.isFinite(own) ? own : 0);
    const noise = playedNoise(oa, ob, a, b, ctx.info.extra);
    const tolerance = Math.max(BAKE_TOLERANCE * span, quantum, noise, 1e-9);
    let dev = 0;
    for (let k = 0; k < REVERSE_CHECK_SAMPLES; k++) {
      const x = (k + 0.5) / REVERSE_CHECK_SAMPLES;
      const orig = playedRaw(oa, ob, mirroredX(ta + (tb - ta) * x), ctx);
      if (!rawFinite(orig)) continue;
      const rev = playedRaw(a, b, x, ctx);
      const d = rawFinite(rev) ? valueDiff(rev, orig) : Number.POSITIVE_INFINITY;
      if (!Number.isNaN(d)) dev = Math.max(dev, d);
    }
    if (dev > tolerance) {
      const rawAt = (sec: number): RawValue => segmentRaw(oa, ob, mirroredX(sec), ctx);
      const baked = adaptiveBakeCuts(
        a.t,
        b.t,
        a.vOut,
        b.vIn,
        ctx,
        rawAt,
        Math.max(BAKE_TOLERANCE * span, quantum, 1e-9),
      );
      bakeSegmentWith(keys, iS + j, baked.points);
      report = worseSplit(report, { exactness: 'bake', maxRelError: span > 0 ? baked.maxError / span : 0 });
    } else {
      // Expo / Elastic: a step of the slice's staircase counts as a change (splitKey.ts `staircaseChange`).
      const shown = Math.max(dev, staircaseChange(oa, ob, ctx.info.extra));
      if (span > 0 && shown > Math.max(exactNoise(oa, ob, a, b, ctx.info.extra), quantum) + EXACT_REL_ERROR * span) {
        report = worseSplit(report, { exactness: 'approx', maxRelError: shown / span });
      }
    }
  }
  return report;
}

/** Uniform samples per reversed text segment before its character changes are located (at most). */
export const REVERSE_TEXT_SAMPLES = 256;
/** Fewest samples per reversed text segment when many segments share the sample pool. */
const REVERSE_TEXT_MIN_SAMPLES = 64;
/** Samples all measurements of one reversal share (each segment is measured twice: before and after). */
const REVERSE_TEXT_SAMPLE_POOL = 32768;
/** Evaluations all measurements of one reversal share for locating character changes. */
export const REVERSE_TEXT_MAX_EVALS = 40000;

/**
 * Text segments reversed: each one shows (`displayOn`, runs of constant text through textDisplay.ts) what the
 * original showed at the mirrored beat. Reversing a type-on gives an erase with the same rounding, so its characters
 * change at the mirrored moments; an extend becomes a shrink, which rounds its character count where the extend
 * truncated it, so its characters change up to half a character's time apart. A shift beyond
 * TEXT_EXACT_SHIFT_SEC makes the result approximate, with that shift (`textShiftSec`).
 * The whole reversal spends one evaluation budget (REVERSE_TEXT_MAX_EVALS, shared equally by the segments still to
 * measure, `textRunsShared`) and one sample pool, so reversing many long counters at once stays fast; segments
 * measured coarsely report a shift that is an upper bound (textDisplay.ts `textShift`).
 */
function checkReversedText(
  keys: readonly KeyRec[],
  iS: number,
  old: readonly KeyRec[],
  sumBeats: number,
  ctx: CurveCtx,
): SplitReport {
  const n = old.length - 1;
  const tempo = ctx.tempo;
  const extra = ctx.info.extra;
  const oldSecs = old.map((k) => tempo.secAt(k.t));
  const mirrored = (sec: number): number => tempo.secAtBeatF64(sumBeats - tempo.beatF64At(sec));
  const samples = Math.min(
    REVERSE_TEXT_SAMPLES,
    Math.max(REVERSE_TEXT_MIN_SAMPLES, Math.floor(REVERSE_TEXT_SAMPLE_POOL / Math.max(1, 2 * n))),
  );
  const pool: EvalBudget = { left: REVERSE_TEXT_MAX_EVALS };
  let shift = 0;
  let rel = 0;
  for (let j = 0; j < n; j++) {
    const a = keys[iS + j];
    const b = keys[iS + j + 1];
    if (a === undefined || b === undefined) continue;
    const ta = tempo.secAt(a.t);
    const tb = tempo.secAt(b.t);
    if (!(tb > ta)) continue;
    // Measurements still to make, this one included: before and after of this segment and of every later one.
    const parts = 2 * (n - j);
    const before = textRunsShared(pool, parts, (sec) => displayOn(old, oldSecs, mirrored(sec), extra), ta, tb, samples);
    const after = textRunsShared(
      pool,
      parts - 1,
      (sec) => displayedText(a, b, (sec - ta) / (tb - ta), extra),
      ta,
      tb,
      samples,
    );
    const d = textShift(before, after, tb);
    if (d > shift) {
      shift = d;
      rel = d / (tb - ta);
    }
  }
  return shift > TEXT_EXACT_SHIFT_SEC ? { exactness: 'approx', maxRelError: rel, textShiftSec: shift } : EXACT_SPLIT;
}

// ---------------------------------------------------------------------------------------------------------------
// Lead-ins moved by time operations
// ---------------------------------------------------------------------------------------------------------------

/** The first segment of a track whose lead-in Phira extrapolates from it, captured before a time operation. */
export interface LeadInBefore {
  readonly a: KeyRec;
  readonly b: KeyRec;
  /** End of the visible lead-in compared afterwards: [0, until), at most the first key. */
  readonly until: Beat;
}

/**
 * Captures the first segment of `keys` when Phira continues it backwards before the first key (lead-in rule
 * 'extrapolate', first key after beat 0) and that continuation is not constant; `until` (> 0) is where the
 * operation starts changing the curve (insert: `at`; remove: `from`). Null when no lead-in can change.
 */
export function captureLeadIn(keys: readonly KeyRec[], ctx: CurveCtx, until: Beat): LeadInBefore | null {
  const a = keys[0];
  const b = keys[1];
  if (a === undefined || b === undefined || ctx.leadIn !== 'extrapolate') return null;
  if (!Beats.lt(Beats.ZERO, a.t) || !Beats.lt(Beats.ZERO, until)) return null;
  if (a.ease.kind === 'hold' || sameValue(a.vOut, b.vIn)) return null;
  return { a: { ...a }, b: { ...b }, until: Beats.min(until, a.t) };
}

/** Samples of the lead-in comparison (each of the compared range, the old lead-in and the old first segment). */
const LEAD_IN_SHIFT_SAMPLES = 256;

/**
 * How much a time operation changed the visible lead-in captured by `captureLeadIn`: the old first segment (old
 * tempo map) against the new first segment of `after` (new tempo map), both continued backwards as Phira plays
 * them, over [0, until), relative to the value range of the old first segment (the curve being continued; a
 * lead-in extrapolated far, or through a Bezier solver's garbage outside [0, 1], can reach values that would hide
 * any change). Where only one of the two is finite the change counts as the whole range. Exact when the first
 * segment did not move or the change is within f32 noise; otherwise approximate (`approx: 'leadInShift'`).
 */
export function leadInShiftReport(
  before: LeadInBefore,
  after: readonly KeyRec[],
  oldCtx: CurveCtx,
  newCtx: CurveCtx,
): SplitReport {
  const na = after[0];
  const nb = after[1];
  const { a, b } = before;
  if (na === undefined || nb === undefined) return EXACT_SPLIT;
  if (Beats.eq(na.t, a.t) && Beats.eq(nb.t, b.t) && na.ease === a.ease) return EXACT_SPLIT;
  const t0 = oldCtx.tempo.secAt(a.t);
  const t1 = oldCtx.tempo.secAt(b.t);
  const lo = oldCtx.tempo.secAt(Beats.ZERO);
  const hi = oldCtx.tempo.secAt(before.until);
  const n0 = newCtx.tempo.secAt(na.t);
  const n1 = newCtx.tempo.secAt(nb.t);
  if (!(hi > lo) || !(t1 > t0) || !(n1 > n0)) return EXACT_SPLIT;
  const oldAt = (sec: number): RawValue => playedRaw(a, b, (sec - t0) / (t1 - t0), oldCtx);
  const seen: RawValue[] = [a.vOut, b.vIn];
  let dev = 0;
  let broken = false;
  for (let j = 0; j <= LEAD_IN_SHIFT_SAMPLES; j++) {
    const x = j / LEAD_IN_SHIFT_SAMPLES;
    const sec = lo + (hi - lo) * x;
    seen.push(oldAt(t0 + (t1 - t0) * x));
    const was = oldAt(sec);
    const now = playedRaw(na, nb, (sec - n0) / (n1 - n0), newCtx);
    // A lead-in extrapolated beyond f32 shows nothing usable: becoming (or ceasing to be) that is a full change.
    if (rawFinite(was) !== rawFinite(now)) broken = true;
    const d = valueDiff(was, now);
    if (Number.isFinite(d)) dev = Math.max(dev, d);
  }
  const span = valueSpan(seen);
  const rel = broken ? Math.max(span > 0 ? dev / span : 0, NON_FINITE_CHANGE) : span > 0 ? dev / span : 0;
  return rel > EXACT_REL_ERROR ? { exactness: 'approx', maxRelError: rel, approx: 'leadInShift' } : EXACT_SPLIT;
}

// ---------------------------------------------------------------------------------------------------------------
// Insert / remove time
// ---------------------------------------------------------------------------------------------------------------

/**
 * Inserts `length` beats at `at` (>= 0): keys at or after `at` move later. When the first key sat exactly at
 * beat `at` = 0, a Stay key at beat 0 keeps its value through the inserted time. Returns the number of moved keys.
 */
export function insertTimeKeys(keys: KeyRec[], at: Beat, length: Beat): number {
  let moved = 0;
  const first = keys[0];
  const firstAtZero = first !== undefined && Beats.eq(first.t, Beats.ZERO) && Beats.eq(at, Beats.ZERO);
  for (const k of keys) {
    if (Beats.lte(at, k.t)) {
      k.t = Beats.add(k.t, length);
      moved++;
    }
  }
  if (firstAtZero) keys.unshift(newRec(Beats.ZERO, first.vIn, first.vIn, HOLD));
  return moved;
}

export interface RemoveCounts {
  removed: number;
  moved: number;
  /** The edge splits of a cut (exact when the range was shrunk). */
  report: SplitReport;
  /** True when the range held no key and the spanning segment was shrunk (keys at or after `to` moved). */
  shrunk: boolean;
  /**
   * True when that shrunk segment started before `from` and is a curve (not a Stay, not constant): its part before
   * `from` changes too (it is the inverse of insertTime's stretch, not a cut), which the command notes.
   */
  shrunkCurve: boolean;
}

/**
 * True when removing [from, to) CUTS this key list and joins two sides: a key lies inside the range and another
 * one before it. (A range whose only inside key is the first key just trims the start: nothing is joined, unless
 * `ctx` says the first key carries an extrapolated lead-in, which the cut keeps as the left side.)
 */
export function cutJoinsSides(keys: readonly KeyRec[], from: Beat, to: Beat, ctx?: CurveCtx): boolean {
  const first = keys[0];
  if (first === undefined) return false;
  if (ctx !== undefined && Beats.lt(first.t, to) && leadInBeforeSpan(keys, from, ctx)) return true;
  for (let i = 1; i < keys.length; i++) {
    const t = (keys[i] as KeyRec).t;
    if (Beats.lte(to, t)) return false;
    if (Beats.lte(from, t)) return true;
  }
  return false;
}

/**
 * Removes [from, to) from the key list (see the file header). `forceCut`: cut even when no key lies inside the
 * range (the other position axis of an X/Y-linked line was cut there), as long as there is a side before `from` to
 * join; without one, cutting and shrinking are the same. A cut whose range holds (or lies before) the first key of
 * an extrapolated lead-in keeps that lead-in as the left side (`explicitLeadIn`), so the curve before `from` stays
 * as it was.
 */
export function removeTimeKeys(
  keys: KeyRec[],
  from: Beat,
  to: Beat,
  ctx: CurveCtx,
  unlinked: Set<number>,
  forceCut = false,
): RemoveCounts {
  const counts: RemoveCounts = { removed: 0, moved: 0, report: EXACT_SPLIT, shrunk: false, shrunkCurve: false };
  const length = Beats.sub(to, from);
  const inside = (t: Beat): boolean => Beats.lte(from, t) && Beats.lt(t, to);
  const firstKey = keys[0];
  if (firstKey === undefined) return counts;
  // A side before `from` to join a forced cut to: earlier keys, or an extrapolated lead-in (made explicit below).
  const leftToJoin = Beats.lt(firstKey.t, from) || leadInBeforeSpan(keys, from, ctx);
  if (!keys.some((k) => inside(k.t)) && !(forceCut && leftToJoin)) {
    const iSpan = indexAtOrBefore(keys, from);
    const a = keys[iSpan];
    const b = keys[iSpan + 1];
    counts.shrunkCurve =
      a !== undefined && b !== undefined && Beats.lt(a.t, from) && a.ease.kind !== 'hold' && !sameValue(a.vOut, b.vIn);
    for (const k of keys) {
      if (Beats.lte(to, k.t)) {
        k.t = Beats.sub(k.t, length);
        counts.moved++;
      }
    }
    counts.shrunk = counts.moved > 0;
    return counts;
  }
  // The range holds the first key (or a forced cut lies before it): an extrapolated lead-in before it becomes the
  // left side of the cut.
  counts.report = explicitLeadIn(keys, from, ctx, unlinked);
  const hasLeftSide = Beats.lt((keys[0] as KeyRec).t, from);
  const lastKey = keys[keys.length - 1] as KeyRec;
  const hasRightSide = Beats.lte(to, lastKey.t);
  if (hasRightSide) counts.report = worseSplit(counts.report, insertKeyRec(keys, to, ctx, unlinked));
  let arrival: TrackValue | null = null;
  if (hasLeftSide) {
    const r = insertKeyRec(keys, from, ctx, unlinked);
    counts.report = worseSplit(counts.report, r);
    arrival = (keys[r.index] as KeyRec).vIn;
  }
  // The value held after the last removed key (used when nothing follows the range).
  let heldAfter: TrackValue | null = null;
  const kept: KeyRec[] = [];
  for (const k of keys) {
    if (inside(k.t)) {
      if (k.id !== null) counts.removed++;
      unlinkInto(k, unlinked);
      heldAfter = k.vOut;
      continue;
    }
    kept.push(k);
  }
  const iTo = indexAt(kept, to);
  if (iTo >= 0) {
    const b = kept[iTo] as KeyRec;
    b.vIn = arrival ?? b.vOut;
  } else if (heldAfter !== null) {
    const c = newRec(to, arrival ?? heldAfter, heldAfter, HOLD);
    // `to` is shifted back to `from` below together with the later keys.
    kept.push(c);
    kept.sort((x, y) => Beats.cmp(x.t, y.t));
  }
  for (const k of kept) {
    if (Beats.lte(to, k.t)) {
      k.t = Beats.sub(k.t, length);
      if (k.id !== null) counts.moved++;
    }
  }
  keys.splice(0, keys.length, ...kept);
  return counts;
}
